/**
 * Connect a document written before: io.ai proposes which passage answers
 * which question, the writer changes any match or leaves it, and Apply wraps
 * each passage in its question's block. No text changes; anything left
 * unmatched simply stays outside the blocks and is never pushed.
 */
import { ArrowLeftIcon } from "@/design/icons";
import { type FC, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/agents-v2/utils/cn";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/design/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/design/ui/sheet";
import { Skeleton } from "@/design/ui/skeleton";

import { documentOutlineForMapping, highlightBlock, wrapSpans } from "../document/questionBlocks";
import { NUMBER_CLASS, numberColumnStyle } from "./numberColumn";
import { type Question, mapQuestions } from "../tender/questionsApi";

type Para = { index: number; text: string; level: number | null };
type Span = { start: number; end: number } | null;

export type MappingSheetProps = {
  open: boolean;
  bidId: string;
  token: string;
  questions: Question[];
  onClose: () => void;
  onApplied: (count: number) => void;
};

export const MappingSheet: FC<MappingSheetProps> = (props) => (
  <Sheet open={props.open} onOpenChange={(o) => !o && props.onClose()}>
    <SheetContent side="right" className="w-full max-w-none gap-0 p-0 sm:max-w-none [&>button]:hidden">
      {props.open && <Body {...props} />}
    </SheetContent>
  </Sheet>
);

const Body: FC<MappingSheetProps> = ({ bidId, token, questions, onClose, onApplied }) => {
  const numberStyle = numberColumnStyle(questions.map((x) => x.section_number));
  const [paras, setParas] = useState<Para[] | null>(null);
  const [spans, setSpans] = useState<Record<string, Span>>({});
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const outline = await documentOutlineForMapping();
        setParas(outline);
        const matches = await mapQuestions(bidId, outline, token);
        const next: Record<string, Span> = {};
        for (const m of matches) next[m.section_id] = { start: m.start, end: m.end };
        setSpans(next);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Couldn't match the document");
      }
    })();
  }, [bidId, token]);

  const byIndex = useMemo(() => new Map((paras ?? []).map((p) => [p.index, p])), [paras]);
  const headings = useMemo(() => (paras ?? []).filter((p) => p.level), [paras]);
  const lastIndex = paras?.length ? paras[paras.length - 1].index : 0;

  const spanFromHeading = (h: Para): Span => {
    const after = headings.find((x) => x.index > h.index && (x.level ?? 9) <= (h.level ?? 9));
    const end = after ? after.index - 1 : lastIndex;
    return end >= h.index + 1 ? { start: h.index + 1, end } : null;
  };

  const headingAbove = (span: Span) => (span ? [...headings].reverse().find((h) => h.index < span.start) : undefined);
  const paragraphsIn = (span: Span) =>
    span ? (paras ?? []).filter((p) => p.index >= span.start && p.index <= span.end).length : 0;

  const covered = new Set<number>();
  for (const s of Object.values(spans)) if (s) for (let i = s.start; i <= s.end; i += 1) covered.add(i);
  const outside = headings.filter(
    (h) =>
      (h.level ?? 9) <= 2 &&
      !covered.has(h.index + 1) &&
      !Object.values(spans).some((s) => s && headingAbove(s)?.index === h.index)
  );
  const matched = Object.values(spans).filter(Boolean).length;

  const apply = async () => {
    setApplying(true);
    try {
      const list = questions
        .filter((q) => spans[q.section_id])
        .map((q) => ({
          sectionId: q.section_id,
          start: (spans[q.section_id] as { start: number }).start,
          end: (spans[q.section_id] as { end: number }).end,
          title: [q.section_number, q.heading].filter(Boolean).join(" · "),
        }));
      const count = await wrapSpans(bidId, list);
      onApplied(count);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't connect the document");
      setApplying(false);
    }
  };

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex h-11 shrink-0 items-center gap-1 border-b border-line px-2">
        <button
          type="button"
          aria-label="Back"
          onClick={onClose}
          className="flex size-7 items-center justify-center rounded-sm text-fg-2 hover:bg-subtle hover:text-fg"
        >
          <ArrowLeftIcon className="size-4" />
        </button>
        <span className="text-sm font-medium text-fg-3">Connect this document</span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="px-4 pb-2 pt-4">
          <SheetTitle className="text-lg font-semibold leading-snug text-fg">Match answers to questions</SheetTitle>
          <SheetDescription className="mt-0.5 text-sm text-fg-3">
            {paras && !error
              ? `io.ai found answers for ${matched} of ${questions.length} questions. Apply, or change any match first. You can change them later too.`
              : "io.ai is reading the document…"}
          </SheetDescription>
        </div>
        {error && (
          <div className="mx-3 mb-2 rounded-sm border border-danger/30 bg-danger-soft px-2.5 py-2 text-xs text-danger">
            {error}
          </div>
        )}
        {!paras && !error ? (
          <div className="flex flex-col gap-3 px-4 py-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          questions.map((q, i) => {
            const span = spans[q.section_id] ?? null;
            const heading = headingAbove(span);
            const first = span
              ? (byIndex.get(span.start) ?? (paras ?? []).find((p) => p.index >= span.start))
              : undefined;
            return (
              <div
                key={q.section_id}
                className={cn("flex items-start gap-2.5 px-3 py-2", i && "border-t border-line/70")}
                onMouseEnter={() => span && void highlightPreview(span)}
              >
                <span className={cn(NUMBER_CLASS, "pt-px")} style={numberStyle}>
                  {q.section_number}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={cn("block truncate font-medium", !span && "text-fg-3")}>
                    {q.heading || "Untitled question"}
                  </span>
                  <span className="block text-xs leading-4 text-fg-3">
                    {span
                      ? `Under “${heading?.text ?? "document start"}” · ${paragraphsIn(span)} paragraph${paragraphsIn(span) === 1 ? "" : "s"} `
                      : "No matching passage · "}
                    <PassagePicker
                      label={span ? "Change" : "Choose passage"}
                      headings={headings}
                      onPick={(h) => setSpans((s) => ({ ...s, [q.section_id]: h ? spanFromHeading(h) : null }))}
                    />
                  </span>
                  {first && (
                    <span className="mt-1 block truncate border-l-2 border-line-strong pl-2 font-serif text-xs leading-[17px] text-fg-2">
                      {first.text}
                    </span>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>
      <div className="shrink-0 border-t border-line px-3 pb-3 pt-2.5">
        {outside.length > 0 && (
          <p className="mb-2 text-xs leading-[17px] text-fg-3">
            Not part of any answer:{" "}
            {outside
              .slice(0, 5)
              .map((h) => `“${h.text}”`)
              .join(", ")}
            {outside.length > 5 ? ` and ${outside.length - 5} more` : ""}. They stay in the document and are never
            pushed.
          </p>
        )}
        <div className="flex items-center gap-2">
          <span className="flex-1 text-xs tabular-nums text-fg-2">
            {matched} matched · {questions.length - matched} left out
          </span>
          <button
            type="button"
            disabled={!matched || applying}
            onClick={() => void apply()}
            className="rounded-md bg-accent px-3.5 py-1.5 text-sm font-semibold text-fg-inverse hover:bg-accent-hover disabled:opacity-40"
          >
            {applying ? "Applying…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hovering a proposed match selects nothing and writes nothing; it only
// scrolls Word to the passage by selecting its start.
const highlightPreview = async (span: { start: number; end: number }) => {
  try {
    await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();
      paragraphs.items[span.start]?.getRange("Start").select();
      await context.sync();
    });
  } catch {
    // preview only
  }
};
void highlightBlock;

const PassagePicker: FC<{ label: string; headings: Para[]; onPick: (h: Para | null) => void }> = ({
  label,
  headings,
  onPick,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button type="button" className="font-semibold text-accent-hover hover:underline">
        {label}
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="max-h-72 w-64 overflow-y-auto">
      {headings.map((h) => (
        <DropdownMenuItem
          key={h.index}
          onSelect={() => onPick(h)}
          style={{ paddingLeft: 8 + ((h.level ?? 1) - 1) * 10 }}
        >
          <span className="truncate">{h.text}</span>
        </DropdownMenuItem>
      ))}
      <DropdownMenuItem onSelect={() => onPick(null)} className="text-fg-3">
        Leave this question out
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
